import { useRouter } from 'next/router';
import { useCallback, useEffect } from 'react';
import { t } from 'react-switch-lang';
import PaymentProcessContextProvider, { DISPATCHERS, useInitStateFromSessionStorage, usePaymentProcessContext } from '../../context/PaymentProcessContext';
import MainLayout from '../../components/MainLayout';
import PaymentLayout from '../../components/PaymentProcess/PaymentLayout';
import loadClient from '../../utils/AwsFetchClient';
import autofillVals from '../../utils/DebugConstants';
import { isDevelopment } from '../../utils/HostingEnv';
import { scrollToTop } from '../../utils/Accessibility';
import AccountDetails from '../../components/PaymentProcess/Steps/AccountDetails';
import PaymentOption from '../../components/PaymentProcess/Steps/PaymentOption';
import Review from '../../components/PaymentProcess/Steps/Review';
import CardWithLoadingIndicator from '../../components/CardWithLoadingIndicator';
import { STEP } from '../../utils/Constants';
import { redirectToError, validateAccountDetailsForInvoice, validateSmartPaymentLink } from '../../utils/SmartPaymentLink';
import Redirect from '../../components/Redirect';
import { toErrorPage } from '../../components/ErrorDisplay';

/**
 * @typedef {Object} PageProps
 * @property {import('../../context/PaymentProcessContext').PartnerInfo} partnerInfo
 * @property {Object} [autofill]
 * @property {import('../../utils/SmartPaymentLink').SmartPaymentLinkQueryParams} [prepopulate]
 * @property {boolean} [isHostedPage]
 */

/**
 * @param {import('next').GetServerSidePropsContext} context
 * @return {Promise<import('next').GetServerSidePropsResult<PageProps>>}
 */
export async function getServerSideProps({ params, query, locale, req }) {
  const aws = await loadClient();
  const apiResp = await aws.fetch(
    `${process.env.NEXT_PUBLIC_API_URL}/v1/GetPartnerInfo/${params.seqOrVanity}`,
    {
      method: 'GET',
      headers: {
        'x-api-key': process.env.NEXT_PUBLIC_API_KEY,
      },
    }
  ).then((r) => r.json());

  /** @type {import('../../context/PaymentProcessContext').PartnerInfo} */
  const partnerInfo = apiResp.Partners?.[0];

  if (apiResp.Result === 80) {
    return {
      redirect: {
        destination: `/${locale}/maintenance`,
      },
    };
  }

  if (apiResp.Result !== 0) {
    // eslint-disable-next-line no-console
    console.log({
      Message: `GetPartnerInfo failed for: ${params.seqOrVanity}`,
      apiResp,
    });

    return { redirect: redirectToError(locale) };
  }

  if (!partnerInfo) {
    // eslint-disable-next-line no-console
    console.log({
      Message: `GetPartnerInfo not found for: ${params.seqOrVanity}`,
      apiResp,
    });

    // https://nextjs.org/docs/pages/api-reference/functions/get-server-side-props#notfound
    return { notFound: true };
  }

  if (
    !partnerInfo.PaymentOptions ||
    partnerInfo.PaymentOptions.length === 0 ||
    !partnerInfo.Active
  ) {
    return { redirect: redirectToError(locale, 'Error_PartnerUnavailable') };
  }

  const props = { partnerInfo };
  let prepopulate = null;
  try {
    prepopulate = await validateSmartPaymentLink(query, partnerInfo, aws, locale);
  } catch (redirect) {
    return { redirect };
  }
  if (req.headers.referer) {
    prepopulate ??= {};
    prepopulate.referrer = req.headers.referer;
  }

  if (isDevelopment) props.autofill = autofillVals(partnerInfo.PartnerSeq);
  if (isDevelopment) props.autofillPennyFail = autofillVals(partnerInfo.PartnerSeq, true);
  if (prepopulate) props.prepopulate = prepopulate;

  return { props };
}

function mapHashToStep(hash) {
  switch (hash) {
    case 'payment-option':
      return STEP.PAYMENT_OPTION;
    case 'review':
      return STEP.REVIEW;
    case 'make-payment':
      return STEP.MAKE_PAYMENT;
    default:
      return STEP.DETAILS;
  }
}

/** @param {PageProps} props */
export default function BeginPayment({
  partnerInfo,
  autofill = null,
  autofillPennyFail = null,
  prepopulate = null,
  isHostedPage = false,
  hostedPagePayload = null,
}) {
  const router = useRouter();
  const {
    dispatchPaymentProcess,
    paymentProcess: {
      step,
      paymentOption,
      accountDetails,
      isSessionLoaded,
    },
  } = usePaymentProcessContext();

  useInitStateFromSessionStorage(
    partnerInfo ? DISPATCHERS.SET_PARTNER_INFO(
      partnerInfo,
      prepopulate,
      typeof window === 'object' ? mapHashToStep(window.location.hash.slice(1)) : undefined
    ) : null,
    partnerInfo?.PartnerSeq
  );

  useEffect(() => {
    if (!prepopulate || isHostedPage) return;
    // remove query string from URL, to hide smart payment link params
    router.replace(`${window.location.pathname}${window.location.hash}`, undefined, { shallow: true });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => scrollToTop(), [step]);

  const handlePopState = useCallback(({ as }) => {
    const [pathname, hash] = as.split('#');

    // handle popstate if navigating to different route
    if (!pathname.endsWith(router.asPath.split('#')[0])) return true;

    const nextStep = mapHashToStep(hash);

    // going back is always allowed
    if (nextStep < step) return true;
    // don't handle popstate if same step
    if (nextStep === step) return false;
    // don't handle popstate if going forward without proper context values
    if (nextStep > step) {
      if (nextStep === STEP.PAYMENT_OPTION) return !!accountDetails;
      if (nextStep === STEP.REVIEW) return !!accountDetails && !!paymentOption;
    }
    return false;
  }, [accountDetails, paymentOption, step, router.asPath]);

  useEffect(() => {
    function handleHashChange(url) {
      const nextStep = mapHashToStep(url.split('#')[1]);
      dispatchPaymentProcess(DISPATCHERS.SET_PAYMENT_STEP(nextStep));
    }

    router.events.on('hashChangeStart', handleHashChange);

    return () => router.events.off('hashChangeStart', handleHashChange);
  }, [router, dispatchPaymentProcess]);

  useEffect(() => {
    router.beforePopState(handlePopState);

    return () => router.beforePopState();
  }, [handlePopState, router]);

  let entryType;
  if (prepopulate && Object.keys(prepopulate).some((k) => k !== 'referrer')) {
    entryType = prepopulate.siteMode === 'Intuit' ? 'Intuit Smart Payment Link' : 'General Smart Payment Link';
  }

  let content;
  let titleKey = '';
  let allowIndexing = true;
  switch (step) {
    case STEP.DETAILS:
      titleKey = 'PageTitle_AccountDetails';
      content = (
        <AccountDetails
          autofill={autofill}
          autofillPennyFail={autofillPennyFail}
          entryType={entryType}
          isHostedPage={isHostedPage}
          hostedPagePayload={hostedPagePayload}
        />
      );
      break;
    case STEP.PAYMENT_OPTION:
      titleKey = 'PageTitle_PaymentOptions';
      content = <PaymentOption />;
      break;
    case STEP.REVIEW:
      titleKey = 'PageTitle_Review';
      content = <Review />;
      break;
    case STEP.MAKE_PAYMENT:
      titleKey = 'PageTitle_MakePayment';
      content = <CardWithLoadingIndicator />; // actual content is on /complete-payment page
      break;
    case STEP.CONFIRMATION:
      titleKey = 'PageTitle_Confirmation';
      content = <CardWithLoadingIndicator />; // actual content is on /complete-payment page
      break;
    default:
      break;
  }

  const Fields = partnerInfo?.PSWebConfig.Fields;
  const partnerSetForInvoice = Fields?.some((f) => f.DBField === 'InvoiceNo');
  // If the partner is set for invoice, the page is not publicly accessible,
  // so we don't allow indexing by search engines.
  if (partnerSetForInvoice) {
    allowIndexing = false;
  }
  // If the partner is set for invoice, but prepoulate doesn't include invoiceno,
  // then the query params were empty and no validation occurred during getServerSideProps.
  // This is to allow users to refresh the page, in which case we have to
  // validate the accountDetails instead on the client side, once loaded from sessionStorage.
  if (partnerSetForInvoice && !prepopulate?.invoiceno) {
    if (!isSessionLoaded) { // show loading indicator until session is loaded
      content = <CardWithLoadingIndicator />;
    } else if (!validateAccountDetailsForInvoice(accountDetails, Fields)) {
      content = (
        <>
          <CardWithLoadingIndicator />
          <Redirect path={toErrorPage(t('SmartLinkRequiredOrInvalid'))} />
        </>
      );
    }
  }

  return (
    <MainLayout
      showPaymentInfoButton={step !== STEP.REVIEW}
      title={t(titleKey)}
      hideNavAndFooter={isHostedPage}
      siteName={isHostedPage ? t('BillerHostedPageTitle') : t('OfficialSiteName')}
      allowIndexing={allowIndexing}
    >
      <PaymentLayout
        hidePaymentInfo={step === STEP.REVIEW || step === STEP.CONFIRMATION}
        isHostedPage={isHostedPage}
      >
        {content}
      </PaymentLayout>
    </MainLayout>
  );
}

BeginPayment.getLayout = function getLayout(page) {
  return (
    <PaymentProcessContextProvider
      initializer={DISPATCHERS.SET_PARTNER_INFO(
        page.props.partnerInfo,
        page.props.prepopulate
      )}
    >
      {page}
    </PaymentProcessContextProvider>
  );
};
